import { ChevronDownIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useListOngoingAssessments } from '@/api/assessments.hook'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

import { getAssessmentName } from '@/lib/assessment/assessment-name'
import { sortByTimestamp } from '@/lib/date/date'
import { buildAssessmentUrl } from '@/lib/url/urls'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'

type AssessmentBreadcrumbSwitcherProps = {
  companyId: string
  currentAssessmentId: string
  ongoingAssessment: OngoingAssessment
}

export const AssessmentBreadcrumbSwitcher = ({
  companyId,
  currentAssessmentId,
  ongoingAssessment,
}: AssessmentBreadcrumbSwitcherProps) => {
  const navigate = useNavigate()
  const { ongoingAssessments } = useListOngoingAssessments(companyId, !!companyId)

  const items = useMemo(() => {
    const currentIsListed = ongoingAssessments.some((a) => a.id === currentAssessmentId)
    const assessments = sortByTimestamp(
      currentIsListed ? ongoingAssessments : [...ongoingAssessments, ongoingAssessment],
      (a) => a.createdAt,
    )

    return assessments.map((a) => ({
      id: a.id,
      label:
        getAssessmentName({
          projectName: a.projectName?.trim(),
          createdAt: a.createdAt,
        }) || 'Assessment',
    }))
  }, [ongoingAssessments, currentAssessmentId, ongoingAssessment])

  const triggerLabel =
    getAssessmentName({
      projectName: ongoingAssessment.projectName?.trim(),
      createdAt: ongoingAssessment.createdAt,
    }) || 'Assessment'

  return (
    <DropdownSelect
      items={items}
      selectedItems={[currentAssessmentId]}
      onItemSelect={(id) => {
        if (id !== currentAssessmentId) {
          navigate(buildAssessmentUrl(id))
        }
      }}
      popoverAlign='start'
      trigger={
        <Button variant='ghost' className='!px-2 !py-0'>
          <span className='min-w-0 truncate'>{triggerLabel}</span>
          <ChevronDownIcon className='size-5 shrink-0 text-main-foreground-tertiary' />
        </Button>
      }
    />
  )
}
